import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Mail, Phone, ThumbsDown, ThumbsUp, User } from "lucide-react";
import { toast } from "react-toastify";

import { Badge } from "../../../components/Badge";
import { Button } from "../../../components/Button";
import { CustomSelector } from "../../../components/CustomSelector";
import { CustomTextArea } from "../../../components/CustomTextArea";
import { CustomTextField } from "../../../components/CustomTextField";
import { useAuth } from "../../../components/contexts/AuthContext";
import {
  DEAL_STATUS,
  clientifyErrorMessage,
  createClientifyDeal,
  deleteClientifyDeal,
  getClientifyDeal,
  updateClientifyDeal,
} from "../../../services/clientifyService";
import { UnderlineTabs } from "../../Clients/metrics/UnderlineTabs";
import { ClientifySidePanel, PanelField } from "./ClientifySidePanel";
import { DealActivityTimeline } from "./DealActivityTimeline";
import { ClientifyDeleteModal, DeleteIconButton } from "./ClientifyDeleteModal";
import { ContactAutocomplete } from "./ClientifyFormFields";

const CURRENCY_OPTIONS = ["EUR", "USD", "GBP", "MXN", "COP", "ARS", "CLP", "PEN"].map(
  (code) => ({ value: code, label: code })
);

const DETAIL_TABS = [
  { value: "info", label: "Información" },
  { value: "activity", label: "Todos" },
];

const STATUS_BADGE_COLORS = { 1: "blue", 2: "orange", 3: "green", 4: "red" };

const formatMoney = (amount, currency) => {
  const value = Number.parseFloat(amount);
  if (!Number.isFinite(value)) return "Sin importe";
  return value.toLocaleString("es-ES", {
    style: "currency",
    currency: (currency || "EUR").toUpperCase(),
  });
};

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("es-ES", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

/** Name, amount and client of the deal, above the tabs. */
const DealHeader = ({ deal, tab, onTabChange }) => (
  <Box>
    <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }} flexWrap="wrap">
        {deal.pipeline_stage_desc && <Badge variant="secondary">{deal.pipeline_stage_desc}</Badge>}
        {deal.status_desc && (
          <Badge variant="secondary" color={STATUS_BADGE_COLORS[deal.status]}>
            {deal.status_desc}
          </Badge>
        )}
      </Stack>
      <Typography sx={{ fontSize: "1.375rem", fontWeight: 650, lineHeight: 1.25 }}>
        {deal.name || "Sin título"}
      </Typography>
      <Stack direction="row" spacing={2.5} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
        <Typography sx={{ fontSize: "1.0625rem", fontWeight: 600, color: "success.main" }}>
          {formatMoney(deal.amount, deal.currency)}
        </Typography>
        {deal.contact_name && (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary" }}>
            <User size={15} />
            <Typography sx={{ fontSize: "0.9375rem" }}>{deal.contact_name}</Typography>
          </Stack>
        )}
        {deal.company_name && (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "text.secondary" }}>
            <Building2 size={15} />
            <Typography sx={{ fontSize: "0.9375rem" }}>{deal.company_name}</Typography>
          </Stack>
        )}
      </Stack>
    </Box>
    <UnderlineTabs tabs={DETAIL_TABS} value={tab} onChange={onTabChange} />
  </Box>
);

DealHeader.propTypes = {
  deal: PropTypes.object.isRequired,
  tab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
};

/** Read-only "label | value" row, aligned with the editable PanelFields. */
const InfoRow = ({ label, children }) => (
  <PanelField label={label}>
    <Typography sx={{ fontSize: "0.875rem", pt: { xs: 0, sm: "9px" }, wordBreak: "break-word" }}>
      {children || (
        <Box component="span" sx={{ color: "text.disabled" }}>
          —
        </Box>
      )}
    </Typography>
  </PanelField>
);

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const inDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const emptyForm = (stageId) => ({
  name: "",
  amount: "",
  currency: "EUR",
  pipeline_stage_id: stageId ?? null,
  expected_closed_date: inDays(7),
  contact: null,
  remarks: "",
});

const formFromDeal = (deal) => ({
  name: deal.name || "",
  amount: deal.amount ?? "",
  currency: (deal.currency || "EUR").toUpperCase(),
  pipeline_stage_id: deal.pipeline_stage_id ?? null,
  expected_closed_date: deal.expected_closed_date || "",
  contact: deal.contact_id
    ? { id: deal.contact_id, full_name: deal.contact_name, email: deal.contact_email }
    : null,
  remarks: deal.remarks || "",
});

const toPayload = (form) => ({
  name: form.name.trim(),
  amount: form.amount === "" ? null : form.amount,
  currency: form.currency,
  pipeline_stage_id: form.pipeline_stage_id,
  expected_closed_date: form.expected_closed_date || null,
  contact_id: form.contact?.id ?? null,
  remarks: form.remarks,
});

/** Only what changed, so an edit never rewrites fields nobody touched. */
const changedFields = (next, initial) =>
  Object.fromEntries(
    Object.entries(next).filter(
      ([key, value]) => JSON.stringify(value ?? null) !== JSON.stringify(initial[key] ?? null)
    )
  );

/**
 * "Agregar oportunidad" / "Editar oportunidad" side panel.
 *
 * `dealId` null creates; `initialStageId` preselects the column the panel was
 * opened from. There is no owner field: a deal is created for the caller, or
 * for a superadmin, for the owner picked in the page filter (`ownerUserId`,
 * `ownerClientifyId`).
 */
export const DealFormPanel = ({
  open,
  dealId,
  initialStageId,
  stages,
  ownerUserId,
  ownerClientifyId,
  onClose,
  onSaved,
  onDeleted,
  onRequestClose,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isSuperadmin = user?.role === "superadmin";
  const isEdit = Boolean(dealId);
  const needsOwner = !isEdit && isSuperadmin && !ownerUserId;
  const [form, setForm] = useState(() => emptyForm(initialStageId));
  const [initialPayload, setInitialPayload] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("info");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open) setTab("info");
  }, [open, dealId]);

  const { data: deal, isLoading: dealLoading } = useQuery({
    queryKey: ["clientify-deal", dealId],
    queryFn: () => getClientifyDeal(dealId),
    enabled: open && isEdit,
    staleTime: 0,
    gcTime: 0,
  });

  // Filled once per opening: a later refetch must not wipe what the user is typing.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    if (isEdit) {
      if (!deal) return;
      const next = formFromDeal(deal);
      setForm(next);
      setInitialPayload(toPayload(next));
    } else {
      setForm(emptyForm(initialStageId ?? stages[0]?.id));
      setInitialPayload(null);
    }
    initializedRef.current = true;
  }, [open, isEdit, deal, initialStageId, stages]);

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const stageOptions = useMemo(
    () => stages.map((stage) => ({ value: stage.id, label: stage.name })),
    [stages]
  );

  const canSubmit =
    form.name.trim().length > 0 && Boolean(form.pipeline_stage_id) && !needsOwner;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const payload = changedFields(toPayload(form), initialPayload || {});
        if (!Object.keys(payload).length) {
          toast.info("No hay cambios que guardar");
          return;
        }
        const saved = await updateClientifyDeal(dealId, payload);
        toast.success("Oportunidad actualizada");
        onSaved(saved, deal);
        // The panel stays open on the saved deal: header, form and timeline.
        queryClient.setQueryData(["clientify-deal", dealId], saved);
        queryClient.invalidateQueries({ queryKey: ["clientify-deal-activity", dealId] });
        const next = formFromDeal(saved);
        setForm(next);
        setInitialPayload(toPayload(next));
      } else {
        const saved = await createClientifyDeal({
          ...toPayload(form),
          owner_user: isSuperadmin ? ownerUserId : undefined,
        });
        toast.success("Oportunidad creada");
        onSaved(saved, deal);
        onClose();
      }
    } catch (error) {
      toast.error(clientifyErrorMessage(error, "No se pudo guardar la oportunidad"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteClientifyDeal(dealId);
      toast.success("Oportunidad eliminada");
      setConfirmDelete(false);
      onDeleted(deal);
      onClose();
    } catch (error) {
      toast.error(clientifyErrorMessage(error, "No se pudo eliminar la oportunidad"));
    } finally {
      setDeleting(false);
    }
  };

  const isOpenDeal =
    deal && [DEAL_STATUS.open, DEAL_STATUS.expired].includes(String(deal.status));
  const showActivity = isEdit && tab === "activity";

  return (
    <>
    <ClientifySidePanel
      open={open}
      title={isEdit ? "Oportunidad" : "Agregar oportunidad"}
      onClose={onClose}
      onSubmit={handleSubmit}
      canSubmit={canSubmit}
      saving={saving}
      loading={isEdit && dealLoading}
      width={isEdit ? 780 : 640}
      header={isEdit && deal ? <DealHeader deal={deal} tab={tab} onTabChange={setTab} /> : null}
      sectionTitle={isEdit ? null : undefined}
      showFooter={!showActivity}
      headerActions={
        isEdit && deal ? (
          <DeleteIconButton
            label="Eliminar oportunidad"
            onClick={() => setConfirmDelete(true)}
            disabled={saving || deleting}
          />
        ) : null
      }
      footerStart={
        isEdit && isOpenDeal ? (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRequestClose(deal, "won")}
              disabled={saving}
              sx={{ color: "success.main", gap: 0.75 }}
            >
              <ThumbsUp size={15} /> Ganada
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRequestClose(deal, "lost")}
              disabled={saving}
              sx={{ color: "error.main", gap: 0.75 }}
            >
              <ThumbsDown size={15} /> Perdida
            </Button>
          </>
        ) : null
      }
    >
      {showActivity ? (
        <DealActivityTimeline dealId={dealId} />
      ) : (
      <>
      <PanelField label="Nombre de la oportunidad" required>
        <CustomTextField value={form.name} onChange={(e) => set("name")(e.target.value)} />
      </PanelField>

      {needsOwner && (
        <Typography sx={{ fontSize: "0.8125rem", color: "warning.main" }}>
          Selecciona un propietario en el filtro de la página para crear la oportunidad.
        </Typography>
      )}

      <PanelField label="Importe">
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <CustomTextField
              type="number"
              value={form.amount}
              onChange={(e) => set("amount")(e.target.value)}
              min="0"
              step="0.01"
            />
          </Box>
          <Box sx={{ width: 110 }}>
            <CustomSelector
              value={form.currency}
              onChange={set("currency")}
              options={CURRENCY_OPTIONS}
            />
          </Box>
        </Box>
      </PanelField>

      <PanelField label="Etapa" required>
        <CustomSelector
          placeholder="Selecciona una etapa"
          value={form.pipeline_stage_id}
          onChange={set("pipeline_stage_id")}
          options={stageOptions}
        />
      </PanelField>

      <PanelField label="Fecha esperada de cierre">
        <Box sx={{ maxWidth: 220 }}>
          <CustomTextField
            type="date"
            value={form.expected_closed_date}
            onChange={(e) => set("expected_closed_date")(e.target.value)}
          />
        </Box>
      </PanelField>

      <PanelField label="Contacto">
        <ContactAutocomplete
          value={form.contact}
          onChange={set("contact")}
          ownerId={isSuperadmin ? ownerClientifyId : undefined}
        />
      </PanelField>

      <PanelField label="Descripción">
        <CustomTextArea
          rows={5}
          placeholder="Escribe una descripción aquí..."
          value={form.remarks}
          onChange={(e) => set("remarks")(e.target.value)}
        />
      </PanelField>

      {isEdit && deal && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <InfoRow label="Estado">{deal.status_desc}</InfoRow>
          <InfoRow label="Probabilidad">{deal.probability_desc}</InfoRow>
          <InfoRow label="Empresa">{deal.company_name}</InfoRow>
          <InfoRow label="Email del contacto">
            {deal.contact_email && (
              <Stack direction="row" spacing={0.75} alignItems="center" component="span">
                <Mail size={14} />
                <a href={`mailto:${deal.contact_email}`}>{deal.contact_email}</a>
              </Stack>
            )}
          </InfoRow>
          <InfoRow label="Teléfono del contacto">
            {deal.contact_phone && (
              <Stack direction="row" spacing={0.75} alignItems="center" component="span">
                <Phone size={14} />
                <a href={`tel:${deal.contact_phone}`}>{deal.contact_phone}</a>
              </Stack>
            )}
          </InfoRow>
          <InfoRow label="Origen">{deal.deal_source}</InfoRow>
          <InfoRow label="Pipeline">{deal.pipeline_desc}</InfoRow>
          {String(deal.status) === DEAL_STATUS.lost && (
            <InfoRow label="Motivo de pérdida">
              {typeof deal.lost_reason === "object" ? deal.lost_reason?.name : deal.lost_reason}
            </InfoRow>
          )}
          <InfoRow label="Creada">{formatDateTime(deal.created)}</InfoRow>
          <InfoRow label="Última modificación">{formatDateTime(deal.modified)}</InfoRow>
        </>
      )}
      </>
      )}

    </ClientifySidePanel>

    <ClientifyDeleteModal
      open={confirmDelete}
      noun="oportunidad"
      name={deal?.name}
      detail="con su actividad"
      deleting={deleting}
      onCancel={() => setConfirmDelete(false)}
      onConfirm={handleDelete}
    />
    </>
  );
};

DealFormPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  dealId: PropTypes.number,
  initialStageId: PropTypes.number,
  stages: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onDeleted: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  ownerUserId: PropTypes.number,
  ownerClientifyId: PropTypes.number,
};
